import {useEffect} from "react";
import {Link, useNavigate} from "react-router-dom";
import {useAuth} from "../context/AuthContext";

const FEATURES = [
    {icon: "bi-journal-text",  color: "#6366f1", label: "Notes",       desc: "Write and organise notes in folders with rich text and summaries."},
    {icon: "bi-bookmark",      color: "#3b82f6", label: "Bookmarks",   desc: "Save links with tags, favicons, and instant search."},
    {icon: "bi-check2-square", color: "#10b981", label: "Todo",        desc: "Create task lists with due dates, linked to your notes."},
    {icon: "bi-kanban",        color: "#f59e0b", label: "Project Log", desc: "Track project progress with timestamped log entries."},
    {icon: "bi-folder",        color: "#8b5cf6", label: "Storage",     desc: "Upload and manage files in S3-backed folders."},
    {icon: "bi-search",        color: "#ec4899", label: "Search",      desc: "Find anything across all sections instantly."},
];

export default function Home() {
    const {user, loading} = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!loading && user) navigate("/dashboard", {replace: true});
    }, [user, loading, navigate]);

    if (loading || user) return null;

    return (
        <div style={{minHeight: "100vh", background: "linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)"}}>
            {/* Hero */}
            <div className="d-flex flex-column align-items-center justify-content-center text-center px-3 pt-5 pb-4">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="72" height="72" style={{borderRadius: 18, marginBottom: "1.5rem"}}>
                    <defs>
                        <linearGradient id="hg" x1="0" y1="0" x2="1" y2="1">
                            <stop offset="0%" stopColor="#6366f1"/>
                            <stop offset="100%" stopColor="#8b5cf6"/>
                        </linearGradient>
                    </defs>
                    <rect width="512" height="512" rx="108" fill="url(#hg)"/>
                    <path fillRule="evenodd" fill="white"
                        d="M 110,100 L 195,100 A 215,156 0 0 1 195,412 L 110,412 Z M 165,158 L 195,158 A 145,98 0 0 1 195,354 L 165,354 Z"/>
                </svg>
                <div className="mb-3" style={{background: "rgba(99,102,241,0.15)", borderRadius: 16, padding: "0.6rem 1rem", display: "inline-block"}}>
                    <span style={{color: "#a5b4fc", fontSize: "0.85rem", fontWeight: 600, letterSpacing: "0.05em"}}>YOUR PERSONAL WORKSPACE</span>
                </div>
                <h1 style={{fontWeight: 800, fontSize: "clamp(2.2rem, 6vw, 3.5rem)", color: "#fff", lineHeight: 1.1, marginBottom: "1rem"}}>
                    Everything you need,<br/>
                    <span style={{background: "linear-gradient(90deg, #818cf8, #c084fc)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent"}}>
                        in one place.
                    </span>
                </h1>
                <p style={{color: "rgba(255,255,255,0.55)", fontSize: "1.15rem", maxWidth: 480, marginBottom: "2rem", lineHeight: 1.6}}>
                    Danova brings your notes, tasks, bookmarks, projects, and files together in a clean, fast workspace.
                </p>
                <div className="d-flex gap-3 justify-content-center flex-wrap">
                    <Link to="/register" className="btn btn-lg px-5 py-2 fw-semibold" style={{background: "linear-gradient(135deg, #6366f1, #8b5cf6)", color: "#fff", border: "none", borderRadius: 10}}>
                        Get started free
                    </Link>
                    <Link to="/login" className="btn btn-lg px-5 py-2 fw-semibold" style={{background: "rgba(255,255,255,0.08)", color: "#fff", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 10}}>
                        Sign in
                    </Link>
                </div>
            </div>

            {/* Feature grid */}
            <div className="container pb-5" style={{maxWidth: 900}}>
                <div className="row g-3 justify-content-center">
                    {FEATURES.map(f => (
                        <div key={f.label} className="col-sm-6 col-md-4">
                            <div style={{
                                background: "rgba(255,255,255,0.04)",
                                border: "1px solid rgba(255,255,255,0.08)",
                                borderRadius: 14,
                                padding: "1.4rem",
                                height: "100%",
                                transition: "background .2s",
                            }}
                                onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.08)"}
                                onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.04)"}
                            >
                                <div style={{
                                    width: 40, height: 40, borderRadius: 10, marginBottom: "0.85rem",
                                    background: `${f.color}22`, display: "flex", alignItems: "center", justifyContent: "center",
                                }}>
                                    <i className={`bi ${f.icon}`} style={{color: f.color, fontSize: "1.2rem"}}/>
                                </div>
                                <div style={{color: "#fff", fontWeight: 600, marginBottom: "0.35rem"}}>{f.label}</div>
                                <div style={{color: "rgba(255,255,255,0.45)", fontSize: "0.875rem", lineHeight: 1.5}}>{f.desc}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
